// Small controlled selects shared by the S3 line inspector and outputs panel.
// Products/UoMs come from the api-client feeds; these render options and coerce
// the empty option to `""` so an unselected picker stays valid under the parent
// form's own required checks.

import { shortId } from "../../../../../lib/short-id";
import type { components } from "@assessly/api-client";

type Product = components["schemas"]["Product"];
type Uom = components["schemas"]["Uom"];

export function ProductSelect({
  id,
  products,
  value,
  onChange,
  placeholder,
  disabled = false,
  exclude,
}: {
  id?: string | undefined;
  products: readonly Product[];
  value: string;
  onChange: (productId: string) => void;
  placeholder: string;
  disabled?: boolean | undefined;
  exclude?: ReadonlySet<string> | undefined;
}) {
  const options = exclude !== undefined ? products.filter((p) => !exclude.has(p.id)) : products;
  return (
    <select
      id={id}
      className="bom-picker"
      value={value}
      disabled={disabled}
      onChange={(e) => {
        onChange(e.target.value);
      }}
    >
      <option value="">{placeholder}</option>
      {options.map((p) => (
        <option value={p.id} key={p.id}>
          {p.sku} — {p.name}
        </option>
      ))}
    </select>
  );
}

export function UomSelect({
  id,
  uoms,
  value,
  onChange,
  disabled = false,
}: {
  id?: string | undefined;
  uoms: readonly Uom[];
  value: string;
  onChange: (uomId: string) => void;
  disabled?: boolean | undefined;
}) {
  return (
    <select
      id={id}
      className="bom-picker"
      value={value}
      disabled={disabled}
      onChange={(e) => {
        onChange(e.target.value);
      }}
    >
      {uoms.map((u) => (
        <option value={u.id} key={u.id}>
          {u.code}
        </option>
      ))}
    </select>
  );
}

/** Build the id→product map the tree/diff use to resolve SKUs. */
export function indexProducts(products: readonly Product[]): Map<string, Product> {
  return new Map(products.map((p) => [p.id, p]));
}

/** A `uomId → code` labeller; falls back to a short id when the UoM is unknown. */
export function makeUomLabeller(uoms: readonly Uom[]): (uomId: string | null) => string {
  const byId = new Map(uoms.map((u) => [u.id, u.code]));
  return (uomId) => {
    if (uomId === null || uomId.length === 0) return "";
    return byId.get(uomId) ?? shortId(uomId);
  };
}
